import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import "./AnalyticsDashboard.css";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";

import { formatAdminDate, formatCompactNumber } from "./adminConfig";

const CHART_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
  "#14b8a6",
  "#84cc16",
  "#f97316",
];

async function fetchAllRows(table, columns) {
  const pageSize = 1000;
  let from = 0;
  let rows = [];

  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select(columns)
      .order("id", { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) throw error;

    const batch = data || [];
    rows = rows.concat(batch);

    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return rows;
}

async function fetchCategoryRows(table, categoryId, columns) {
  const pageSize = 1000;
  let from = 0;
  let rows = [];

  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select(columns)
      .eq("category_id", categoryId)
      .order("id", { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) throw error;

    const batch = data || [];
    rows = rows.concat(batch);

    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return rows;
}

function buildCategoryBreakdown(rows, categoryMap) {
  const counts = {};

  rows.forEach((row) => {
    const categoryId = row.category_id || "unknown";
    const name = categoryMap[categoryId] || "Unknown";
    const key = categoryId;

    if (!counts[key]) {
      counts[key] = { id: categoryId, name, value: 0 };
    }

    counts[key].value += 1;
  });

  return Object.values(counts).sort((a, b) => b.value - a.value);
}

function buildActivitySeries(itemsRows, userItemRows) {
  const days = {};
  const now = new Date();

  for (let offset = 29; offset >= 0; offset -= 1) {
    const date = new Date(now);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - offset);
    const key = date.toISOString().slice(0, 10);
    days[key] = { date: key, catalog: 0, userItems: 0 };
  }

  itemsRows.forEach((row) => {
    if (!row.created_at) return;
    const key = new Date(row.created_at).toISOString().slice(0, 10);
    if (days[key]) days[key].catalog += 1;
  });

  userItemRows.forEach((row) => {
    if (!row.created_at) return;
    const key = new Date(row.created_at).toISOString().slice(0, 10);
    if (days[key]) days[key].userItems += 1;
  });

  return Object.values(days);
}

export default function AnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalItems: 0,
    totalUserItems: 0,
    unlinkedUserItems: 0,
  });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [userItemBreakdown, setUserItemBreakdown] = useState([]);
  const [topBrands, setTopBrands] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [activitySeries, setActivitySeries] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [selectedChartKey, setSelectedChartKey] = useState("");

  const subcategoryMap = useMemo(
    () => Object.fromEntries(subcategories.map((subcategory) => [subcategory.id, subcategory.name])),
    [subcategories]
  );

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const [
        usersCount,
        itemsCount,
        userItemsCount,
        unlinkedCount,
        categoriesResult,
        subcategoriesResult,
        recentUsersResult,
        recentItemsResult,
        itemRows,
        userItemRows,
      ] = await Promise.all([
        supabase.from("users").select("id", { count: "exact", head: true }),
        supabase.from("items").select("id", { count: "exact", head: true }),
        supabase.from("user_items").select("id", { count: "exact", head: true }),
        supabase
          .from("user_items")
          .select("id", { count: "exact", head: true })
          .is("item_id", null),
        supabase.from("categories").select("id, name").order("name"),
        supabase.from("subcategories").select("id, name, category_id").order("name"),
        supabase.from("users").select("*").order("created_at", { ascending: false }).limit(8),
        supabase
          .from("items")
          .select("id, name, brand, set_name, year, created_at")
          .order("created_at", { ascending: false })
          .limit(8),
        fetchAllRows("items", "id, category_id, subcategory_id, brand, created_at"),
        fetchAllRows("user_items", "id, category_id, subcategory_id, created_at"),
      ]);

      const directErrors = [
        usersCount.error,
        itemsCount.error,
        userItemsCount.error,
        unlinkedCount.error,
        categoriesResult.error,
        subcategoriesResult.error,
        recentUsersResult.error,
        recentItemsResult.error,
      ].filter(Boolean);

      if (directErrors.length) throw directErrors[0];

      const categoryRows = categoriesResult.data || [];
      const categoryMap = Object.fromEntries(categoryRows.map((category) => [category.id, category.name]));

      const brandCounts = {};
      itemRows.forEach((row) => {
        const brand = row.brand?.trim();
        if (!brand) return;
        brandCounts[brand] = (brandCounts[brand] || 0) + 1;
      });

      const brandData = Object.entries(brandCounts)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 12);

      setStats({
        totalUsers: usersCount.count || 0,
        totalItems: itemsCount.count || 0,
        totalUserItems: userItemsCount.count || 0,
        unlinkedUserItems: unlinkedCount.count || 0,
      });
      setCategories(categoryRows);
      setSubcategories(subcategoriesResult.data || []);
      setCategoryBreakdown(buildCategoryBreakdown(itemRows, categoryMap));
      setUserItemBreakdown(buildCategoryBreakdown(userItemRows, categoryMap));
      setTopBrands(brandData);
      setRecentUsers(recentUsersResult.data || []);
      setRecentItems(recentItemsResult.data || []);
      setActivitySeries(buildActivitySeries(itemRows, userItemRows));
    } catch (error) {
      console.error("Unable to load admin analytics:", error);
      setErrorMessage(error.message || "Unable to load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const openCategoryDrilldown = async (source, clickedData) => {
    const entry = clickedData?.payload || clickedData;
    if (!entry?.id || entry.id === "unknown") return;

    setSelectedChartKey(`${source}:${entry.id}`);
    setDrawerLoading(true);
    setDrawer({
      title: `${entry.name} — ${source === "items" ? "Canonical Items" : "User Items"}`,
      type: "category",
      source,
      rows: [],
      subcategoryBreakdown: [],
    });

    try {
      const table = source === "items" ? "items" : "user_items";
      const columns = source === "items"
        ? "id, name, brand, set_name, year, card_number, subcategory_id, created_at"
        : "id, item_id, user_specific_name, user_specific_brand, user_specific_set_name, user_specific_year, subcategory_id, created_at";

      const allCategoryRows = await fetchCategoryRows(table, entry.id, columns);

      const subCounts = {};
      allCategoryRows.forEach((row) => {
        const subId = row.subcategory_id || "unknown";
        const name = subcategoryMap[subId] || "Uncategorized";
        if (!subCounts[subId]) subCounts[subId] = { id: subId, name, count: 0 };
        subCounts[subId].count += 1;
      });

      setDrawer({
        title: `${entry.name} — ${source === "items" ? "Canonical Items" : "User Items"}`,
        type: "category",
        source,
        rows: allCategoryRows
          .slice()
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
          .slice(0, 100),
        total: allCategoryRows.length,
        subcategoryBreakdown: Object.values(subCounts).sort((a, b) => b.count - a.count),
      });
    } catch (error) {
      console.error("Unable to load analytics category drilldown:", error);
      setErrorMessage(error.message || "Unable to load category drilldown.");
    } finally {
      setDrawerLoading(false);
    }
  };

  const openRecentUsers = () => {
    setSelectedChartKey("recent-users");
    setDrawer({
      title: "Recent Users",
      type: "users",
      rows: recentUsers,
      total: stats.totalUsers,
    });
  };

  const openRecentItems = () => {
    setSelectedChartKey("recent-items");
    setDrawer({
      title: "Recent Canonical Items",
      type: "items",
      rows: recentItems,
      total: stats.totalItems,
    });
  };

  const openBrandDrilldown = async (brand) => {
    if (!brand) return;

    setSelectedChartKey(`brand:${brand}`);
    setDrawerLoading(true);
    setDrawer({ title: `Brand: ${brand}`, type: "items", rows: [] });

    const { data, error } = await supabase
      .from("items")
      .select("id, name, brand, set_name, year, card_number, created_at")
      .eq("brand", brand)
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      console.error("Unable to load brand drilldown:", error);
      setErrorMessage(error.message || "Unable to load brand drilldown.");
    } else {
      setDrawer({
        title: `Brand: ${brand}`,
        type: "items",
        rows: data || [],
        total: topBrands.find((item) => item.name === brand)?.total || data?.length || 0,
      });
    }

    setDrawerLoading(false);
  };

  const closeDrawer = () => {
    setDrawer(null);
    setSelectedChartKey("");
  };

  if (loading) {
    return <div className="admin-analytics-loading">Loading analytics...</div>;
  }

  return (
    <div className="admin-analytics-page">
      {errorMessage && <div className="hadmin-error">{errorMessage}</div>}

      <div className="admin-analytics-stat-grid">
        <button
          type="button"
          className={`admin-analytics-stat-card ${selectedChartKey === "recent-users" ? "selected" : ""}`}
          onClick={openRecentUsers}
        >
          <span>👥</span>
          <div>
            <small>Total Users</small>
            <strong>{formatCompactNumber(stats.totalUsers)}</strong>
          </div>
        </button>

        <button
          type="button"
          className={`admin-analytics-stat-card ${selectedChartKey === "recent-items" ? "selected" : ""}`}
          onClick={openRecentItems}
        >
          <span>📦</span>
          <div>
            <small>Canonical Items</small>
            <strong>{formatCompactNumber(stats.totalItems)}</strong>
          </div>
        </button>

        <div className="admin-analytics-stat-card">
          <span>🗂️</span>
          <div>
            <small>User Items</small>
            <strong>{formatCompactNumber(stats.totalUserItems)}</strong>
          </div>
        </div>

        <div className="admin-analytics-stat-card warning">
          <span>↯</span>
          <div>
            <small>Unlinked User Items</small>
            <strong>{formatCompactNumber(stats.unlinkedUserItems)}</strong>
          </div>
        </div>
      </div>

      <section className="admin-analytics-card full-width">
        <div className="admin-analytics-card-header">
          <div>
            <h3>30-Day Activity</h3>
            <p>Canonical catalog additions and user-item additions by day.</p>
          </div>
          <button type="button" className="hadmin-btn-secondary" onClick={fetchDashboardData}>
            Refresh
          </button>
        </div>

        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={activitySeries}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
            <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 11 }} />
            <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
            <Tooltip />
            <Area type="monotone" dataKey="catalog" stroke="#6366f1" fill="#6366f144" />
            <Area type="monotone" dataKey="userItems" stroke="#06b6d4" fill="#06b6d433" />
          </AreaChart>
        </ResponsiveContainer>
      </section>

      <div className="admin-analytics-chart-grid">
        <section className="admin-analytics-card">
          <div className="admin-analytics-card-header">
            <div>
              <h3>Canonical Categories</h3>
              <p>Click a slice to inspect real category records.</p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Pie
                data={categoryBreakdown}
                dataKey="value"
                nameKey="name"
                outerRadius={115}
                onClick={(entry) => openCategoryDrilldown("items", entry)}
              >
                {categoryBreakdown.map((entry, index) => (
                  <Cell
                    key={entry.id}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                    stroke={selectedChartKey === `items:${entry.id}` ? "#fff" : "transparent"}
                    strokeWidth={selectedChartKey === `items:${entry.id}` ? 3 : 1}
                    opacity={selectedChartKey && !selectedChartKey.startsWith(`items:${entry.id}`) ? 0.6 : 1}
                    style={{ cursor: entry.id === "unknown" ? "default" : "pointer" }}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value) => Number(value).toLocaleString()} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </section>

        <section className="admin-analytics-card">
          <div className="admin-analytics-card-header">
            <div>
              <h3>User-Item Categories</h3>
              <p>Click a slice to inspect user collection activity.</p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Pie
                data={userItemBreakdown}
                dataKey="value"
                nameKey="name"
                outerRadius={115}
                onClick={(entry) => openCategoryDrilldown("user_items", entry)}
              >
                {userItemBreakdown.map((entry, index) => (
                  <Cell
                    key={entry.id}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                    stroke={selectedChartKey === `user_items:${entry.id}` ? "#fff" : "transparent"}
                    strokeWidth={selectedChartKey === `user_items:${entry.id}` ? 3 : 1}
                    opacity={selectedChartKey && !selectedChartKey.startsWith(`user_items:${entry.id}`) ? 0.6 : 1}
                    style={{ cursor: entry.id === "unknown" ? "default" : "pointer" }}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value) => Number(value).toLocaleString()} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </section>
      </div>

      <section className="admin-analytics-card full-width">
        <div className="admin-analytics-card-header">
          <div>
            <h3>Top Canonical Brands</h3>
            <p>Click a bar to view up to 100 canonical records for that brand.</p>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={topBrands} margin={{ left: 4, right: 10, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
            <XAxis
              dataKey="name"
              angle={-35}
              textAnchor="end"
              interval={0}
              tick={{ fill: "#94a3b8", fontSize: 11 }}
            />
            <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="total" onClick={(entry) => openBrandDrilldown(entry?.name || entry?.payload?.name)}>
              {topBrands.map((entry, index) => (
                <Cell
                  key={entry.name}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                  stroke={selectedChartKey === `brand:${entry.name}` ? "#fff" : "transparent"}
                  strokeWidth={selectedChartKey === `brand:${entry.name}` ? 2 : 0}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </section>

      {drawer && (
        <>
          <div className="hadmin-drawer-backdrop" onClick={closeDrawer} />
          <aside className="hadmin-drawer admin-analytics-drawer">
            <div className="hadmin-drawer-header">
              <div>
                <h2>{drawer.title}</h2>
                <p>{drawer.total !== undefined ? `${formatCompactNumber(drawer.total)} total` : "Analytics detail"}</p>
              </div>
              <button type="button" className="hadmin-icon-btn" onClick={closeDrawer}>
                ×
              </button>
            </div>

            <div className="hadmin-drawer-body">
              {drawerLoading ? (
                <div className="hadmin-empty">Loading drilldown...</div>
              ) : (
                <>
                  {drawer.subcategoryBreakdown?.length > 0 && (
                    <div className="admin-analytics-subcategory-grid">
                      {drawer.subcategoryBreakdown.map((subcategory) => (
                        <div className="hadmin-detail-box" key={subcategory.id}>
                          <span>{subcategory.name}</span>
                          <strong>{formatCompactNumber(subcategory.count)}</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="hadmin-list" style={{ marginTop: 16 }}>
                    {(drawer.rows || []).map((row, index) => (
                      <div className="hadmin-list-item" key={row.id || index}>
                        {drawer.type === "users" ? (
                          <>
                            <strong>{row.username || row.email || "User"}</strong>
                            <div className="hadmin-muted" style={{ marginTop: 4, fontSize: 11 }}>
                              {row.email || row.id} • {formatAdminDate(row.created_at)}
                            </div>
                          </>
                        ) : drawer.source === "user_items" ? (
                          <>
                            <strong>{row.user_specific_name || "User item"}</strong>
                            <div className="hadmin-muted" style={{ marginTop: 4, fontSize: 11 }}>
                              {[row.user_specific_year, row.user_specific_brand, row.user_specific_set_name]
                                .filter(Boolean)
                                .join(" • ") || `Linked item: ${row.item_id || "none"}`}
                            </div>
                          </>
                        ) : (
                          <>
                            <strong>{row.name || "Canonical item"}</strong>
                            <div className="hadmin-muted" style={{ marginTop: 4, fontSize: 11 }}>
                              {[row.year, row.brand, row.set_name].filter(Boolean).join(" • ") || "No details"}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
